import React, { useState, useEffect, useMemo } from "react";
import styles from "./UserPermissionsTab.module.scss";
import { MdBlock, MdVisibility, MdEdit } from "react-icons/md";
import Axios from "../../../../../_services/caller.service";
import { toast } from "react-toastify";

const UserPermissionsTab = () => {
  // ─── États ───
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [permissionsData, setPermissionsData] = useState([]);

  // Tableau des IDs d'utilisateurs dont l’accordéon est ouvert
  const [openUserIds, setOpenUserIds] = useState([]);

  // Pour stocker les overrides de chaque utilisateur :
  //   { [userId]: { actionKey: level, ... }, ... }
  const [overridesByUser, setOverridesByUser] = useState({});

  // Pour stocker les permissions effectives renvoyées par l’API pour chaque user :
  //   { [userId]: { actionKey: effectiveLevel, ... }, ... }
  const [effectivePermissionsByUser, setEffectivePermissionsByUser] = useState({});

  // ─── Pagination & filtres ───
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [usersPerPage, setUsersPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  // ─── Chargement initial des utilisateurs (rôle “user”) ───
  const fetchInitialUsers = async () => {
    try {
      const res = await Axios.get("/permissionsUsers?role=user");
      console.log("Utilisateurs chargés :", res.data);
      
      setUsers(res.data);
    } catch (err) {
      console.error("Erreur chargement utilisateurs :", err);
      toast.error("Impossible de charger les utilisateurs");
    }
  };

  // ─── Chargement initial des modules/actions + niveaux autorisés ───
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [modsRes, levelsRes] = await Promise.all([
          Axios.get("/getAllModulesWithActions?role=user"),
          Axios.get("/getAllLevelsWithAccess?role=user"),
        ]);
        
        setPermissionsData(modsRes.data);
        setLevels(levelsRes.data);
      } catch (err) {
        console.error("Erreur chargement permissions :", err);
        toast.error("Impossible de charger les permissions");
      }
    };

    fetchAll();
    fetchInitialUsers();
  }, []);

  // ─── Fonction utilitaire : récupérer les overrides et effectivePermissions d’un user ───
  const loadUserOverridesAndEffective = async (userId) => {
    // 1. Overrides
    Axios.get(`/getUserOverrides/${userId}`)
      .then((res) => {
        setOverridesByUser((prev) => ({
          ...prev,
          [userId]: res.data || {},
        }));
      })
      .catch(() => {
        setOverridesByUser((prev) => ({
          ...prev,
          [userId]: {},
        }));
      });

    // 2. Permissions effectives
    Axios.get(`/getEffectivePermissions/${userId}`)
      .then((res) => {
        setEffectivePermissionsByUser((prev) => ({
          ...prev,
          [userId]: res.data.permissions || {},
        }));
      })
      .catch(() => {
        setEffectivePermissionsByUser((prev) => ({
          ...prev,
          [userId]: {},
        }));
      });
  };

  // ─── Ouverture/fermeture de l’accordéon pour un utilisateur ───
  const toggleOpenUser = (userId) => {
    setOpenUserIds((prev) => {
      if (prev.includes(userId)) {
        // fermer
        return prev.filter((id) => id !== userId);
      } else {
        // ouvrir
        return [...prev, userId];
      }
    });

    // Si on ouvre et qu’on n’a pas encore ses données, on les charge
    if (!overridesByUser[userId] || !effectivePermissionsByUser[userId]) {
      loadUserOverridesAndEffective(userId);
    }
  };

  // ─── Mise à jour du niveau/groupe d’un utilisateur ───
 const handleGroupChange = async (userId, newLevelId) => {
  const parsedId =
    newLevelId !== "" && !isNaN(parseInt(newLevelId))
      ? parseInt(newLevelId)
      : null;

  try {
    await Axios.post("/updateUserSupportLevel", {
      userId,
      supportLevelId: parsedId,
    });

    // Mise à jour en local
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, supportLevelId: parsedId } : u
      )
    );

    toast.success("✅ Groupe mis à jour");
console.log("🛰️ Envoi updateUserSupportLevel :", {
  userId,
  supportLevelId: parsedId,
});

    // Rafraîchir les permissions effectives
    const res = await Axios.get(`/getEffectivePermissions/${userId}`);
    setEffectivePermissionsByUser((prev) => ({
      ...prev,
      [userId]: res.data.permissions || {},
    }));
  } catch (err) {
    console.error("Erreur mise à jour groupe :", err);
    toast.error("Erreur lors de la mise à jour du groupe");
  }
};


  // ─── Cocher/décocher un override pour un user donné ───
  const handleCheckboxToggle = (userId, actionKey, level) => {
    setOverridesByUser((prevAll) => {
      const prev = prevAll[userId] || {};
      const current = prev[actionKey];
      let nextOverrides;
      if (current === level) {
        // décocher → supprimer l’override
        const { [actionKey]: _, ...rest } = prev;
        nextOverrides = rest;
      } else {
        // cocher → override
        nextOverrides = { ...prev, [actionKey]: level };
      }

      // Enregistrer immédiatement en base
      Axios.post("/saveUserOverrides", {
        userId,
        overrides: nextOverrides,
      })
        .then(async () => {
          toast.success("✅ Override mis à jour");
          // Après le succès, on rafraîchit les permissions effectives
          const res = await Axios.get(`/getEffectivePermissions/${userId}`);
          setEffectivePermissionsByUser((prevMap) => ({
            ...prevMap,
            [userId]: res.data.permissions || {},
          }));
        })
        .catch((err) => {
          console.error("Erreur saveUserOverrides :", err);
          toast.error("Erreur enregistrement override");
        });

      return {
        ...prevAll,
        [userId]: nextOverrides,
      };
    });
  };

  // ─── Activer / Désactiver un utilisateur ───
  const toggleUserActive = async (userId, currentActive) => {
    try {
      await Axios.put("/toggleUserActive", { userId, active: !currentActive });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, active: !currentActive } : u
        )
      );
      toast.success("🔄 Statut utilisateur mis à jour");
    } catch (err) {
      console.error("Erreur activation/désactivation :", err);
      toast.error("Erreur serveur");
    }
  };

  // ─── Filtrer & paginer la liste des utilisateurs ───
  const filteredUsers = useMemo(() => {
    return users
      .filter((u) =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((u) => {
        if (selectedGroup === "all") return true;
        if (selectedGroup === "") return u.supportLevelId == null;
        return u.supportLevelId === parseInt(selectedGroup);
      });
  }, [users, searchTerm, selectedGroup]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );


  // Fonction utilitaire pour transformer un nom de groupe en “slug”
  const slugify = (str) => {
    if (!str) return "none";
    // 1) Normaliser et supprimer les diacritiques
    const cleaned = str
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();
    // 2) Remplacer espaces ou tirets par underscores
    return cleaned.replace(/[\s-]+/g, "_");
  };


  // ─── Rendu ───
  return (
    <div className={styles.container}>
      {/* ─── Barre de filtres (recherche + groupe) ─── */}
      <div className={styles.filterRow}>
        <input
          type="text"
          placeholder="Rechercher…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
        <select
          value={selectedGroup}
          onChange={(e) => {
            setSelectedGroup(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">Tous les groupes</option>
          <option value="">Aucun groupe</option>
          {levels.map((lvl) => (
            <option key={lvl.id} value={String(lvl.id)}>
              {lvl.name}
            </option>
          ))}
        </select>
        <select
          value={usersPerPage}
          onChange={(e) => {
            setUsersPerPage(parseInt(e.target.value));
            setCurrentPage(1);
          }}
        >
          {[5, 10, 20].map((n) => (
            <option key={n} value={n}>
              {n} par page
            </option>
          ))}
        </select>
      </div>

      {/* ─── Liste des utilisateurs ─── */}
      <ul className={styles.userList}>
     {paginatedUsers.map((user) => {
  const isOpen = openUserIds.includes(user.id);
  const userOverrides = overridesByUser[user.id] || {};
  const effectivePerms = effectivePermissionsByUser[user.id] || {};


          const levelObj = levels.find((lvl) => lvl.id === user.supportLevelId);

          // → Chaîne brute (ou “none” si pas de groupe)
          const rawGroupName = levelObj ? levelObj.name : "none";

          // → Générer le slug
          const slug = slugify(rawGroupName);

          // → Récupérer la classe CSS Modules correspondante
          const bgClass = styles[`bgGroup_${slug}`] || "";

  return (
    <li
      key={user.id}
      className={`
        ${styles.userListItem}
        ${!user.active ? styles.inactive : ""}
  
      `}
    >
      <div className={`${styles.userSummary} ${bgClass}`}>
        {/* Nom de l’utilisateur */}
        <div className={styles.userName}>{user.name}</div>
        <div className={styles.userName}>{user.email}</div>

        {/* Sélecteur du groupe/niveau */}
        <div className={styles.groupSelectWrapper}>
          <select
            className={styles.groupSelect}
            value={
              user.supportLevelId != null
                ? String(user.supportLevelId)
                : ""
            }
            onChange={(e) =>
              handleGroupChange(user.id, e.target.value)
            }
          >
            <option value="">Aucun groupe</option>
            {levels.map((lvl) => (
              <option key={lvl.id} value={String(lvl.id)}>
                {lvl.name}
              </option>
            ))}
          </select>
        </div>

        {/* Statut actif/inactif */}
        <div className={styles.userStatus}>
          {user.active ? "✅ Actif" : "❌ Inactif"}
        </div>

        {/* Bouton pour ouvrir/fermer l’accordéon des permissions */}
        <button
          className={`${styles.toggleButton} ${
            isOpen ? styles.opened : ""
          }`}
          onClick={() => toggleOpenUser(user.id)}
        >
          {isOpen ? "▲ Masquer" : "▼ Voir les permissions"}
        </button>

        {/* Bouton activer/désactiver */}
        <button
          className={styles.toggleActiveBtn}
          onClick={(e) => {
            e.stopPropagation();
            toggleUserActive(user.id, user.active);
          }}
        >
          {user.active ? "Désactiver" : "Activer"}
        </button>
      </div>

      {/* Accordéon des permissions */}
      {isOpen && (
        <div className={styles.permissionsPanel}>
          {permissionsData.map((mod) => (
            <div key={mod.id} className={styles.moduleCard}>
              <h3>{mod.name}</h3>
              <table className={styles.permissionsTable}>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Description</th>
                    <th>Permissions</th>
                    <th>
                      <MdBlock className={styles.badgeNone} />
                      <br />
                      <span>Aucun</span>
                    </th>
                    <th>
                      <MdEdit className={styles.badgeFull} />
                      <br />
                      <span>Lecture/Écriture</span>
                    </th>
                    <th>
                      <MdVisibility className={styles.badgeRead} />
                      <br />
                      <span>Lecture</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mod.actions.map((action) => {
                    const ovVal = userOverrides[action.key];
                    const prVal = effectivePerms[action.key] ?? 0;

                    return (
                      <tr key={action.key}>
                        <td>{action.key}</td>
                        <td>{action.description || "-"}</td>
                        <td className={styles.previewCell}>
                          {prVal === 0 && (
                            <>
                              <MdBlock className={styles.badgeNone} />
                              <span>Aucun</span>
                            </>
                          )}
                          {prVal === 2 && (
                            <>
                              <MdEdit className={styles.badgeFull} />
                              <span>Lecture/Écriture</span>
                            </>
                          )}
                          {prVal === 1 && (
                            <>
                              <MdVisibility className={styles.badgeRead} />
                              <span>Lecture</span>
                            </>
                          )}
                        </td>
                        {[0, 2, 1].map((val) => (
                          <td key={val}>
                            <input
                              type="checkbox"
                              checked={ovVal === val}
                              onChange={() =>
                                handleCheckboxToggle(
                                  user.id,
                                  action.key,
                                  val
                                )
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </li>
  );
})}


      </ul>

      {/* ─── Contrôles de pagination ─── */}
      <div className={styles.pagination}>
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ⏮️
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          ◀️
        </button>
        <span>
          Page {currentPage} sur {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          ▶️
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          ⏭️
        </button>
      </div>
    </div>
  );
};

export default UserPermissionsTab;
